import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import {
  ATTENDANCE_STATUSES,
  ATTENDANCE_STATUS_LABELS,
  markAttendance,
  updateAttendance,
  type AttendanceRead,
} from '@/features/attendance/api'
import { listEmployees } from '@/features/employees/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

function toLocalInput(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

const schema = z.object({
  employee_id: z.string().trim().min(1, 'Pick an employee'),
  date: z.string().trim().min(1, 'Pick a date'),
  status: z.enum(ATTENDANCE_STATUSES),
  check_in_at: z.string().trim(),
  check_out_at: z.string().trim(),
  notes: z.string().trim().max(2000),
})

type FormValues = z.infer<typeof schema>

/** Mark a new day or correct an existing one. `employee_id`/`date` are fixed once a record
 *  exists (`AttendanceUpdate` carries neither) -- same reasoning as `CandidateFormDialog`'s
 *  locked email: changing either identifies a different row, not an edit to this one. */
export function AttendanceFormDialog({
  open,
  attendance,
  onClose,
}: {
  open: boolean
  attendance?: AttendanceRead
  onClose: () => void
}) {
  const isEdit = attendance !== undefined
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)

  const employees = useQuery({
    queryKey: ['employees', 'active-for-attendance'],
    queryFn: () => listEmployees({ status: 'active' }),
    retry: false,
    enabled: open && !isEdit,
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      employee_id: attendance?.employee_id ?? '',
      date: attendance?.date ?? new Date().toISOString().slice(0, 10),
      status: (attendance?.status as (typeof ATTENDANCE_STATUSES)[number]) ?? 'present',
      check_in_at: toLocalInput(attendance?.check_in_at ?? null),
      check_out_at: toLocalInput(attendance?.check_out_at ?? null),
      notes: attendance?.notes ?? '',
    },
  })

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const shared = {
        status: values.status,
        check_in_at: fromLocalInput(values.check_in_at),
        check_out_at: fromLocalInput(values.check_out_at),
        notes: values.notes || null,
      }
      return attendance
        ? updateAttendance(attendance.id, shared)
        : markAttendance({ employee_id: values.employee_id, date: values.date, ...shared })
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['attendance'] })
      void queryClient.invalidateQueries({ queryKey: ['attendance', saved.id] })
      toast.success(isEdit ? 'Attendance updated.' : 'Attendance marked.')
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : 'Could not reach the server. Try again.')
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  const employeeOptions = employees.data?.data ?? []

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title={isEdit ? 'Correct attendance' : 'Mark attendance'}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="attendance-form" loading={isSubmitting}>
              {isEdit ? 'Save changes' : 'Mark attendance'}
            </Button>
          </>
        }
      >
        <form id="attendance-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Employee" error={errors.employee_id?.message}>
              {(props) =>
                isEdit ? (
                  <Input {...props} value={attendance.employee_name ?? attendance.employee_id} disabled />
                ) : (
                  <select {...props} {...register('employee_id')} className={SELECT_CLASS}>
                    <option value="">Select an employee</option>
                    {employeeOptions.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.full_name ?? employee.employee_code}
                      </option>
                    ))}
                  </select>
                )
              }
            </Field>
            <Field label="Date" error={errors.date?.message}>
              {(props) => (
                <Input {...props} {...register('date')} type="date" disabled={isEdit} />
              )}
            </Field>
          </div>

          <Field label="Status" error={errors.status?.message}>
            {(props) => (
              <select {...props} {...register('status')} className={SELECT_CLASS}>
                {ATTENDANCE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {ATTENDANCE_STATUS_LABELS[status]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Check in" error={errors.check_in_at?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('check_in_at')} type="datetime-local" />}
            </Field>
            <Field label="Check out" error={errors.check_out_at?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('check_out_at')} type="datetime-local" />}
            </Field>
          </div>

          <Field label="Notes" error={errors.notes?.message} hint="Optional.">
            {(props) => <Input {...props} {...register('notes')} autoComplete="off" />}
          </Field>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
